/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

import { SERVICE_REQUEST_LABEL } from './entityLabels';

export const SERVICE_REQUEST_STATUSES = ['open', 'closed'];

export const SERVICE_REQUEST_STATUS_LABELS = {
  open: 'Open',
  closed: 'Closed',
};

export const SERVICE_REQUEST_MEDIA_FILE_TYPES = ['video', 'image', 'audio', 'session_recording'];

export const SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS = {
  video: 'Video',
  image: 'Image',
  audio: 'Audio',
  session_recording: 'Session Recording',
};

// Must match the backend's FindAllServiceRequestsDto sentinel used to filter
// for service requests with no assignedUserUid.
export const UNASSIGNED_FILTER_VALUE = 'unassigned';

export const SERVICE_REQUEST_EVENT_LABELS = {
  created: `${SERVICE_REQUEST_LABEL.singular} created`,
  status_changed: 'Status changed',
  type_changed: 'Type changed',
  hierarchy_changed: 'Options changed',
  customer_name_changed: 'Customer name changed',
  asset_identifier_changed: 'Asset identifier changed',
  equipment_identifier_changed: 'Equipment identifier changed',
  additional_details_changed: 'Additional details changed',
  media_added: 'Media added',
  media_removed: 'Media removed',
  note_added: 'Note added',
  note_updated: 'Note edited',
  note_deleted: 'Note deleted',
  assignee_changed: 'Assignee changed',
  public_link_generated: 'Public link generated',
  public_link_regenerated: 'Public link regenerated',
  public_link_expiry_updated: 'Public link expiry updated',
  public_link_revoked: 'Public link revoked',
  public_link_accessed: 'Public link accessed',
  live_session_joined: 'Live session joined',
  checklist_created: 'Checklist created',
  checklist_deleted: 'Checklist deleted',
  checklist_item_added: 'Checklist item added',
  checklist_item_completed: 'Checklist item completed',
  checklist_item_reopened: 'Checklist item reopened',
  checklist_item_deleted: 'Checklist item deleted',
  checklist_comment_deleted: 'Checklist comment deleted',
};

// Mirrors SERVICE_REQUEST_NOTE_MAX_LENGTH in server/src/service-requests/constants.ts.
export const SERVICE_REQUEST_NOTE_MAX_LENGTH = 5000;

// Mirror the SERVICE_REQUEST_CHECKLIST_* limits in server/src/service-requests/constants.ts.
export const SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH = 255;
export const SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH = 500;
export const SERVICE_REQUEST_CHECKLIST_COMMENT_MAX_LENGTH = SERVICE_REQUEST_NOTE_MAX_LENGTH;

// The server's MAX_CHECKLISTS_PER_SERVICE_REQUEST; the env value can't go above it.
const SERVER_MAX_CHECKLISTS = 20;

/**
 * Checklists allowed per service request, from REACT_APP_SERVICE_REQUEST_MAX_CHECKLISTS:
 * - not set at all       → 0: the checklist feature is hidden everywhere
 * - set but blank        → no limit of its own (only the server's maximum applies)
 * - a number > 0         → that many (capped at the server's maximum); once a request
 *                          has this many, the add-checklist option is hidden
 * - 0 or not a number    → 0: hidden
 */
const parseMaxChecklists = (raw) => {
  if (raw === undefined) return 0;
  if (raw.trim() === '') return SERVER_MAX_CHECKLISTS;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, SERVER_MAX_CHECKLISTS) : 0;
};
const rawMaxChecklists = process.env.REACT_APP_SERVICE_REQUEST_MAX_CHECKLISTS;
export const SERVICE_REQUEST_MAX_CHECKLISTS = parseMaxChecklists(rawMaxChecklists);
export const SERVICE_REQUEST_CHECKLISTS_ENABLED = SERVICE_REQUEST_MAX_CHECKLISTS > 0;
// Blank value: no configured limit, so the UI doesn't advertise one up front.
export const SERVICE_REQUEST_CHECKLISTS_UNLIMITED = rawMaxChecklists !== undefined && rawMaxChecklists.trim() === '';

// Section/tab title: singular when only one checklist is allowed, plural otherwise.
export const SERVICE_REQUEST_CHECKLIST_SECTION_LABEL = SERVICE_REQUEST_MAX_CHECKLISTS === 1 ? 'Checklist' : 'Checklists';
export const SERVICE_REQUEST_CHECKLIST_SECTION_LABEL_LOWER = SERVICE_REQUEST_CHECKLIST_SECTION_LABEL.toLowerCase();
